/**
 * Work holds the box — what the runner says about its hold on every lease beat
 * (spec §3.6). Absent from the beat when the flag is off; `mode` is therefore never
 * 'off'. Times are the runner's clock (ms since epoch), the same convention as vitals.
 */
import { z } from 'zod';

const ms = z.number().int().min(0);

/**
 * How long one hold lives: the runner marks each hold for this many seconds, and work that
 * makes no progress for a whole hold is LAPSED — nothing renews it and the box may freeze
 * (apps/sandbox runner/box-hold.ts). Shared because the server holds a report to the same
 * rule: a count of work with no progress for this long is not a turn (restart rung).
 */
export const HOLD_CAP_S = 900;

export const HoldReportSchema = z.object({
  mode: z.enum(['shadow', 'act']),
  /** A marked process is live right now (shadow: would be). */
  held: z.boolean(),
  heldSinceMs: ms.optional(),
  /** Renewals since boot. */
  renewals: z.number().int().min(0),
  lastProgressAtMs: ms.optional(),
  /** The ActivityManager's task-class work count at the beat. */
  refs: z.number().int().min(0),
  /**
   * Background processes the agent started (a dev server, a watcher, a long job) that the runner
   * is staying alive for, so its box stays awake: work, held for at most 30 minutes after the last
   * turn (apps/sandbox runner/idle-exit.ts BACKGROUND_HOLD_MAX_MS). In either mode — the runner
   * itself is the hold, not a mark. Absent when there are none.
   */
  background: z.number().int().min(0).optional(),
  at: ms,
});
export type HoldReport = z.infer<typeof HoldReportSchema>;
